import useImageUpload from "@/hooks/useImageUpload";
import { FileIcon, UploadIcon } from "@radix-ui/react-icons";
import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  Spinner,
  Text,
} from "@radix-ui/themes";
import React, { useEffect, useRef, useState } from "react";

interface ImageUploaderProps {
  location: string;
  setImageUrl?: (url: string) => void;
  buttonText?: string;
  fieldText?: string;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  location,
  setImageUrl,
  buttonText = "Upload Image",
  fieldText = "Drop your files here, or click to browse",
}) => {
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const { upload, loading, error, imageUrl } = useImageUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedImage(event.target.files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setSelectedImage(e.dataTransfer.files[0]);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleUpload = async () => {
    if (selectedImage) {
      await upload(selectedImage, location);
      setSelectedImage(null);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    if (imageUrl && setImageUrl) {
      setImageUrl(imageUrl);
    }
  }, [imageUrl, setImageUrl]);

  useEffect(() => {
    handleUpload();
  }, [selectedImage]);

  return (
    <Flex direction="column" gap="3">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleUpload();
        }}
        onReset={() => {
          setSelectedImage(null);
        }}
      >
        {!selectedImage && (
          <>
            <Card
              size="5"
              onClick={triggerFileInput}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <Flex direction="column" align="center" justify="center" gap="5">
                <Box>
                  <UploadIcon width={32} height={32} />
                </Box>

                <Heading size="4" as="h2" m="0">
                  {fieldText}
                </Heading>
              </Flex>
            </Card>
            <input
              onChange={handleImageChange}
              type="file"
              hidden
              ref={fileInputRef}
            />
          </>
        )}

        {selectedImage && (
          <Flex direction="column" gap="3">
            <Card size="2" mt="3">
              <Grid columns="4">
                <Flex
                  align="center"
                  gap="3"
                  gridColumn={{ initial: "1 / -1", lg: "1 / 3" }}
                >
                  <Flex
                    style={{
                      backgroundColor: "var(--accent-3)",
                      borderRadius: "var(--radius-2)",
                    }}
                    width="52px"
                    height="52px"
                    align="center"
                    justify="center"
                  >
                    <FileIcon width={24} height={24} />
                  </Flex>
                  <Flex gap="1" direction="column">
                    <Heading size="2" m="0">
                      {selectedImage.name}
                    </Heading>
                    <Text size="1" color="gray">
                      {(selectedImage.size / 1024).toFixed(2)} KB
                    </Text>
                  </Flex>
                </Flex>
                <Flex
                  justify="center"
                  align="center"
                  gridColumn={{ initial: "1 / -1", lg: "3 / 5" }}
                >
                  {loading && (
                    <Text size="2" align="right" color="gray">
                      Uploading...
                    </Text>
                  )}
                  {error && (
                    <Text size="2" align="right" color="red">
                      {error.message}
                    </Text>
                  )}
                </Flex>
              </Grid>
            </Card>

            <Box>
              <Button type="submit" disabled={loading}>
                {loading ? <Spinner /> : buttonText}
              </Button>
            </Box>
          </Flex>
        )}
      </form>
    </Flex>
  );
};

export default ImageUploader;
